// Centralized SEO metadata for public routes.
// - Used by the client-side TitleManager (SPA navigation)
// - Used by the prerender script to generate per-route static <head>

import { PROGRAMMATIC_PAGES, PROGRAMMATIC_PAGE_PATHS } from './programmaticSeo.js';

export const SITE_ORIGIN = 'https://tresopilot.fr';
export const OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

// List of routes that are publicly accessible and should be indexed.
export const PUBLIC_ROUTES = [
  '/',
  '/tarifs',
  '/faq',
  '/contact',
  '/cgu',
  '/confidentialite',
  '/cookies',
  '/mentionslegales',
  '/disclaimer',
  ...PROGRAMMATIC_PAGE_PATHS,
];

const DEFAULT = {
  title: 'TrésoPilot',
  description:
    'TrésoPilot aide les micro-entrepreneurs à suivre leur trésorerie, estimer leur vrai net et anticiper URSSAF/TVA/CFE avec des rappels d’échéances.',
};

const PROGRAMMATIC_ROUTE_META = Object.fromEntries(
  PROGRAMMATIC_PAGES.map((page) => [page.path, {
    title: page.title,
    description: page.description,
    ogTitle: page.title,
    ogDescription: page.description,
  }])
);

const ROUTE_META = {
  '/': {
    title: 'TrésoPilot — Votre cockpit de trésorerie',
    description:
      'Suivez votre trésorerie, estimez votre vrai net (CA → net) et anticipez URSSAF/TVA/CFE avec des rappels d’échéances. Simple, clair, pensé pour les micro-entrepreneurs.',
  },
  '/tarifs': {
    title: 'Tarifs — TrésoPilot | Abonnements & Pack 60 jours',
    description:
      'Découvrez les offres TrésoPilot : Starter gratuit, Plus, Pro et Pack Tranquillité 60 jours. Pilotez votre trésorerie, anticipez URSSAF/TVA/CFE et sachez combien vous pouvez vous verser.',
  },
  '/login': {
    title: 'Connexion — TrésoPilot',
    description:
      'Connectez-vous à TrésoPilot pour consulter vos échéances, suivre votre trésorerie et recevoir vos rappels URSSAF/TVA.',
  },
  '/register': {
    title: 'Créer un compte — TrésoPilot',
    description:
      'Créez votre compte TrésoPilot et commencez à piloter votre trésorerie : estimations, échéances, rappels URSSAF/TVA et tableau de bord.',
  },
  '/faq': {
    title: 'FAQ — TrésoPilot | Réponses rapides',
    description:
      'Réponses rapides sur TrésoPilot : fonctionnement, rappels URSSAF/TVA, sécurité, abonnements et bonnes pratiques.',
  },
  '/contact': {
    title: 'Contact — TrésoPilot | Support',
    description:
      'Une question ? Contactez TrésoPilot. On vous répond rapidement.',
  },
  '/cgu': {
    title: 'CGU — TrésoPilot | Conditions d’utilisation',
    description:
      'Consultez les Conditions Générales d’Utilisation de TrésoPilot.',
  },
  '/confidentialite': {
    title: 'Politique de confidentialité — TrésoPilot',
    description:
      'Découvrez comment TrésoPilot protège vos données et respecte votre confidentialité.',
  },
  '/cookies': {
    title: 'Cookies — TrésoPilot | Consentement',
    description:
      'Informations sur l’utilisation des cookies sur TrésoPilot et vos choix de consentement.',
  },
  '/mentionslegales': {
    title: 'Mentions légales — TrésoPilot',
    description:
      'Mentions légales de TrésoPilot.',
  },
  '/disclaimer': {
    title: 'Disclaimer — TrésoPilot | Limites & responsabilités',
    description:
      'Informations importantes sur les limites et responsabilités liées à l’utilisation de TrésoPilot.',
  },
  ...PROGRAMMATIC_ROUTE_META,
};

export function normalizePathname(pathname = '/') {
  const p = String(pathname || '/');
  const withoutHash = p.split('#')[0];
  const withoutQuery = withoutHash.split('?')[0];
  // Remove trailing slash (except root)
  return withoutQuery !== '/' ? withoutQuery.replace(/\/+$/, '') : '/';
}

export function isPublicRoute(pathname) {
  const p = normalizePathname(pathname);
  return PUBLIC_ROUTES.includes(p);
}

export function getSeoForPath(pathname) {
  const p = normalizePathname(pathname);
  const meta = ROUTE_META[p] || DEFAULT;
  const canonical = `${SITE_ORIGIN}${p}`;

  return {
    pathname: p,
    canonical,
    title: meta.title || DEFAULT.title,
    description: meta.description || DEFAULT.description,
    ogTitle: meta.ogTitle || meta.title || DEFAULT.title,
    ogDescription: meta.ogDescription || meta.description || DEFAULT.description,
    ogImage: OG_IMAGE,
    robots: isPublicRoute(p) ? 'index,follow' : 'noindex,nofollow',
  };
}
